import { useCallback, useEffect, useState } from "react";
import { Alert, Button } from "reactstrap";
import _ from "underscore";

import InformationGroup from "components/Drawers/Fields/InformationGroup";
import FieldSettingsGroup from "components/Drawers/Fields/FieldSettingsGroup";
import AppDrawer from "components/Drawers/AppDrawer";
import FieldUtils from "utils/FieldUtils";
import useFieldTypes from "api/api-hooks/cloud/useFieldTypes";
import FieldDefault from "components/Drawers/Fields/FieldDefault";
import { callFunctionIfExists } from "utils/GeneralUtils";
import SettingsGroupCard from "elements/SettingsGroupCard";
import AppSpinner from "../../../elements/AppSpinner";
import FieldTypeManager from "./FieldTypeSpecific/FieldTypeManager";
import Constants from "../../../utils/Constants";
import FieldTypeSpecific from "./FieldTypeSpecific/FieldTypeSpecific";

const getInitialFieldValues = (subitemParent) => ({
  name: "",
  apiName: "",
  fieldType: "",
  description: "",
  required: false,
  unique: false,
  default: false,
  defaultValue: "",
  defaultSource: "",
  source: "",
  trueDisplayName: "True",
  falseDisplayName: "False",
  hierarchical: false,
  protected: false,
  renameProtected: false,
  readonly: false,
  sensitive: false,
  summary: false,
  summaryFields: {},
  subitemFields: [],
  public: false,
  subitemParent: subitemParent,
  presentation: "Logo",
  subitemRecordNameField: "",
  gcUsers: false,
});

// onClose - Expects a function to update parent's state to close the Modal.
// onSave - Expects a function for handling saves for parent.
// open - Expects parents state for whether or not this modal should be open.
// field - Expects previous field state, also indicates that we are editing a field, and not creating.
const EditFieldDrawer = ({
  onClose,
  onSave,
  open,
  submitting,
  error,
  field = null,
  showModificationProtection = false,
  defaultProtected = false,
  size = "md",
  subitemParent = null, // If provided drawer knows we are editing a subitem field.
  allowFieldTypeEdit = false,
  availableFieldTypes,
  moduleAPIName,
}) => {
  const [page, updatePage] = useState("home");
  const [fieldValues, setFieldValues] = useState(
    getInitialFieldValues(subitemParent)
  );
  const { fieldTypes } = useFieldTypes();
  const [disabled, setDisabled] = useState(false);
  const calculatedDefaultFields = Constants.CALCULATED_FIELD_TYPES.filter(
    (v) => v !== "Calculated"
  );

  const clearState = () => {
    setFieldValues((prev) => ({
      ...prev,
      name: "",
      apiName: "",
      fieldType: "",
      required: false,
      unique: false,
      default: false,
      defaultValue: "",
      defaultSource: "",
      source: "",
      description: "",
      trueDisplayName: "True",
      falseDisplayName: "False",
      protected: showModificationProtection ? defaultProtected : false,
      renameProtected: false,
      readonly: false,
      sensitive: false,
      summary: false,
      summaryFields: {},
      subitemFields: [],
      subitemParent: subitemParent,
      presentation: "Logo",
      subitemRecordNameField: "",
      gcUsers: false,
    }));
  };

  const loadDefaults = useCallback(
    (field) => {
      let fieldDefault;
      let fieldDefaultValue;

      if (
        field.DefaultValue !== null &&
        field.DefaultValue !== undefined &&
        field.DefaultValue !== ""
      ) {
        fieldDefault = true;
        fieldDefaultValue = field.DefaultValue;
      } else {
        fieldDefault = false;
      }

      updatePage("Home");
      setFieldValues({
        name: field.Name,
        apiName: field.APIName,
        fieldType: field.FieldType,
        required: field.Required ? true : false,
        unique: field.Unique ? true : false,
        description: field.Description ?? "",
        default:
          fieldDefault ||
          (calculatedDefaultFields.includes(field.FieldType) &&
            !_.isEmpty(field?.CalculatedFieldData)),
        hierarchical: field?.Hierarchical ?? false,
        defaultValue: fieldDefaultValue,
        defaultSource: field?.DefaultSource || "",
        source: field.SourcesList,
        trueDisplayName: field.TrueDisplayName || "True",
        falseDisplayName: field.FalseDisplayName || "False",
        protected: field.Protected ? true : false,
        renameProtected: field.RenameProtected ?? false,
        readonly: field?.ReadOnly ?? false,
        sensitive: field?.Sensitive ?? false,
        summary: field?.RecordSummary,
        summaryFields: field?.RecordSummaryFields,
        subitemFields: field?.SubitemFields,
        subitemParent: subitemParent,
        presentation: field?.Presentation ?? "Logo",
        subitemRecordNameField: field?.SubitemRecordNameField ?? "",
        public: field?.Public,
        gcWebsite: field?.GcWebsite ?? false,
        calculatedFieldData: field?.CalculatedFieldData ?? {},
        gcUsers: field?.GcUsers ?? false,
      });
    },
    [subitemParent]
  );

  // Whenever the current module/field changes, reload modules.
  useEffect(() => {
    if (!field) return;

    loadDefaults(field);
  }, [field, loadDefaults]);

  const close = async () => {
    clearState();

    callFunctionIfExists(onClose);
  };

  const modifyField = async (e) => {
    e.preventDefault();

    try {
      await onSave(fieldValues.apiName, fieldValues, close);
    } catch (error) {
      console.error(error);
    }
  };

  const hasFieldSpecificSettings = () => {
    if (FieldUtils.fieldTypeIsSubitem(fieldValues.fieldType)) {
      return true;
    }

    if (FieldUtils.fieldTypeHasOptions(fieldTypes, fieldValues.fieldType)) {
      return true;
    }

    if (
      [
        "Toggle",
        "Image",
        "SingleFile",
        "Audio",
        "Video",
        "URL",
        "Filter",
      ].includes(fieldValues.fieldType)
    ) {
      return true;
    }

    return false;
  };

  const setPage = (newPage) => () => updatePage(newPage);

  return (
    <AppDrawer
      open={open}
      onClose={page !== "Home" ? setPage("Home") : close}
      backdrop={page === "Home" ? true : "static"}
      headerTitle={`Modify ${field?.Name} Field`}
      headerDescription="Select a group below to make changes"
      size={size}
      headerOptions={
        page === "Home" ? (
          <Button
            size="sm"
            color="primary"
            onClick={modifyField}
            disabled={submitting}
            style={{ width: 80 }}
          >
            {submitting ? <AppSpinner size="sm" color="white" /> : "Save"}
          </Button>
        ) : (
          <Button
            size="sm"
            onClick={setPage("Home")}
            style={{ width: 80 }}
            disabled={disabled}
          >
            Go Back
          </Button>
        )
      }
      bodyCSS="pt-2 pb-8"
      body={
        <>
          {page === "Home" && (
            <>
              <SettingsGroupCard
                title="Field Information"
                description="Modify basic information about the field, such as name and
                      description."
                onClick={setPage("FieldInformation")}
              />
              {hasFieldSpecificSettings() && (
                <SettingsGroupCard
                  title={`${fieldValues.fieldType} - Specific Settings`}
                  description={`Modify settings specific to ${fieldValues.fieldType} fields.`}
                  onClick={setPage("FieldType")}
                />
              )}
              {allowFieldTypeEdit && (
                <SettingsGroupCard
                  title="Field Type"
                  description="Modify field type for this field"
                  onClick={setPage("SelectFieldType")}
                />
              )}
              {FieldUtils.fieldUsesDefault(fieldValues.fieldType) && (
                <SettingsGroupCard
                  title="Default Value"
                  description="Add or modify the default value for this field."
                  onClick={setPage("DefaultValue")}
                />
              )}
              <SettingsGroupCard
                title="Additional Settings"
                description="Modify additonal settings for this field"
                onClick={setPage("FieldSettings")}
              />
            </>
          )}
          <div className="mt-3">
            {page === "FieldInformation" && (
              <InformationGroup
                fieldValues={fieldValues}
                setFieldValues={setFieldValues}
                disableAPIName
                showModificationProtection={showModificationProtection}
              />
            )}
            {page === "SelectFieldType" && (
              <FieldTypeManager
                fieldValues={fieldValues}
                setFieldValues={setFieldValues}
                disableSubitem={!!subitemParent}
                availableFieldTypes={availableFieldTypes}
              />
            )}
            {page === "FieldType" && (
              <FieldTypeSpecific
                fieldValues={fieldValues}
                setFieldValues={setFieldValues}
                showModificationProtection={showModificationProtection}
                setDisabled={(val) => setDisabled(val)}
                moduleAPIName={moduleAPIName}
                allowFieldTypeEdit={allowFieldTypeEdit}
              />
            )}
            {page === "DefaultValue" && (
              <FieldDefault
                fieldValues={fieldValues}
                setFieldValues={setFieldValues}
                showModificationProtection={showModificationProtection}
                disableSubitem={!!subitemParent}
              />
            )}
            {page === "FieldSettings" && (
              <FieldSettingsGroup
                fieldValues={fieldValues}
                setFieldValues={setFieldValues}
                showModificationProtection={showModificationProtection}
              />
            )}
          </div>
          {error && (
            <div
              style={{
                position: "absolute",
                bottom: 20,
                width: "calc(100% - 50px)",
              }}
            >
              <Alert color="danger">{error}</Alert>
            </div>
          )}
        </>
      }
    />
  );
};

export default EditFieldDrawer;
